/**
 * Thin wrapper around `admin-ajax.php`.
 *
 * Every public function returns a promise resolving to the response payload
 * already unwrapped from the WP success/error envelope. Errors are thrown as
 * ApiError so callers can branch on `code`.
 */

import { config } from './config.js';

export class ApiError extends Error {
    constructor(message, code = 'unknown', status = 0, data = null) {
        super(message);
        this.code = code;
        this.status = status;
        this.data = data;
    }
}

/**
 * The page nonce can go stale without any bug on our side — most commonly
 * when the visitor signs in through the host theme's own login popup
 * (Woodmart) without a page reload: the cookie session changes but
 * `marcConfig.nonce` was minted for the anonymous session. A reload
 * re-localizes the config for the current session, so we do that once,
 * automatically, instead of asking the user to. The timestamp guard keeps
 * a genuinely broken setup (e.g. blocked cookies) from reload-looping.
 */
const NONCE_RELOAD_KEY = 'marc_nonce_reload_at';
const maybeReloadForStaleNonce = () => {
    try {
        const last = Number(sessionStorage.getItem(NONCE_RELOAD_KEY) || 0);
        if (Date.now() - last < 30000) return false;
        sessionStorage.setItem(NONCE_RELOAD_KEY, String(Date.now()));
    } catch {
        // Storage unavailable (private mode) — skip the auto-reload rather
        // than risk a loop we can't detect.
        return false;
    }
    window.location.reload();
    return true;
};

const post = async (action, payload = {}, { signal } = {}) => {
    const body = new FormData();
    body.append('action', action);
    body.append('nonce', config.nonce);

    for (const [key, value] of Object.entries(payload)) {
        if (value === undefined || value === null) continue;
        if (value instanceof File || value instanceof Blob) {
            const filename = value instanceof File ? value.name : undefined;
            if (filename) {
                body.append(key, value, filename);
            } else {
                body.append(key, value);
            }
        } else if (Array.isArray(value)) {
            value.forEach((item) => {
                if (item instanceof File) {
                    body.append(`${key}[]`, item, item.name);
                } else if (item instanceof Blob) {
                    body.append(`${key}[]`, item);
                } else {
                    body.append(`${key}[]`, String(item));
                }
            });
        } else {
            body.append(key, String(value));
        }
    }

    let response;
    try {
        response = await fetch(config.ajaxUrl, {
            method: 'POST',
            credentials: 'same-origin',
            body,
            signal,
        });
    } catch (networkError) {
        throw new ApiError('Network error', 'network_error', 0, networkError);
    }

    let json;
    try {
        json = await response.json();
    } catch {
        throw new ApiError('Invalid response', 'invalid_response', response.status);
    }

    if (!response.ok || json.success === false) {
        const data = json.data ?? {};
        if (data.code === 'invalid_nonce') {
            maybeReloadForStaleNonce();
        }
        throw new ApiError(
            data.message ?? response.statusText ?? 'Request failed',
            data.code ?? 'request_failed',
            response.status,
            data,
        );
    }

    return json.data ?? null;
};

export const api = {
    queryOrder: (orderNumber, options) =>
        post('marc_query_order', { order_number: orderNumber }, options),

    checkOrder: (orderNumber, options) =>
        post('marc_check_order', { order_number: orderNumber }, options),

    submitReview: ({ orderNumber, orderInfo, screenshots, name, phone, email }, options) =>
        post(
            'marc_submit_review',
            {
                order_number: orderNumber,
                order_info: JSON.stringify(orderInfo ?? {}),
                screenshots,
                name,
                phone,
                email,
            },
            options,
        ),

    getHistory: (options) => post('marc_get_history', {}, options),

    accountSummary: (options) => post('marc_account_summary', {}, options),

    login: ({ login, password, remember }, options) =>
        post('marc_login', { login, password, remember: remember ? 1 : 0 }, options),

    register: ({ email, username, password }, options) =>
        post('marc_register', { email, username, password }, options),
};
